import { computed, inject, Injectable, signal } from '@angular/core';
import { Auth, User, user, getIdTokenResult } from '@angular/fire/auth';
import { AuthService } from '../services/auth.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { filter, from, map, pipe, switchMap, tap, withLatestFrom } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { FirebaseError } from '@angular/fire/app';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { InviteUserDialogComponent } from '../components/invite-user-dialog/invite-user-dialog.component';
import { patchState, signalState } from '@ngrx/signals';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomCfError, extractErrorMessage } from '../utils';

interface State {
  users: UserWithCustomClaims[];
  loading: boolean;
  loginError: string | null;
  signingUp: boolean;
}

const initialState: State = {
  users: [],
  loading: false,
  loginError: null,
  signingUp: false
};

export type UserWithCustomClaims = User & {
  customClaims?: Record<string, boolean | string | number>;
  disabled?: boolean;
};

@Injectable()
export class AuthStore {
  readonly #state = signalState(initialState);
  readonly #authService = inject(AuthService);
  readonly #snackBar = inject(MatSnackBar);
  readonly #router = inject(Router);
  readonly #afAuth = inject(Auth);
  readonly #dialog = inject(MatDialog);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly user$ = user(this.#afAuth);

  readonly user = signal<UserWithCustomClaims | null>(null);
  readonly userPhoto = computed(() => {
    const user = this.user();

    if (!user) {
      return '/user.svg';
    }
    if (!user.photoURL) {
      const photoURL = user.providerData.find((provider) => !!provider.photoURL)?.photoURL;

      return photoURL ?? '/user.svg';
    }

    return user.photoURL;
  });

  readonly userName = computed(() => {
    const user = this.user();
    if (!user) {
      return '';
    }
    if (!user.displayName) {
      const displayName = user.providerData.find((provider) => !!provider.displayName)?.displayName;
      return displayName ?? user.email;
    }
    return user.displayName ?? user.email;
  });

  readonly isSuperAdmin = computed(() => {
    const user = this.user();
    if (!user) {
      return false;
    }
    if (user.customClaims?.['super_admin'] === true) {
      return true;
    }
    return false;
  });

  readonly users = computed(() => {
    const users = this.#state.users();
    return users.map((user) => ({
      ...user,
      // customClaims: user. ?? {},
      photoURL:
        user.photoURL ??
        user.providerData.find((provider) => !!provider.photoURL)?.photoURL ??
        '/user.svg',
      displayName:
        user.displayName ??
        user.providerData.find((provider) => !!provider.displayName)?.displayName ??
        null
    }));
  });
  readonly loading = this.#state.loading;
  readonly hasUsers = computed(() => this.#state.users().length > 0);
  readonly loginError = this.#state.loginError;
  readonly signingUp = this.#state.signingUp;

  readonly loginWithPassword = rxMethod<{ email: string; password: string }>(
    pipe(
      tap(() => patchState(this.#state, { loginError: null })),
      switchMap(({ email, password }) =>
        from(this.#authService.login(email, password)).pipe(
          tapResponse(
            () => {
              this.#router.navigate(['/dashboard']);
            },
            (error: FirebaseError) =>
              patchState(this.#state, { loginError: `Failed to login: ${error.code} ` })
          )
        )
      )
    )
  );

  readonly loginWithGoogle = rxMethod<void>(
    pipe(
      tap(() => patchState(this.#state, { loginError: null })),
      switchMap(() =>
        from(this.#authService.loginWithGoogle()).pipe(
          tapResponse(
            () => {
              this.#router.navigate(['/dashboard']);
            },
            (error: FirebaseError) => {
              if (error.code === 'auth/internal-error') {
                const { error: customError } = extractErrorMessage(error) as CustomCfError;
                patchState(this.#state, {
                  loginError: `Failed to login with Google: ${customError?.message ?? error.code}`
                });
                return;
              }

              patchState(this.#state, {
                loginError: `Failed to login with Google: ${error.code}`
              });
            }
          )
        )
      )
    )
  );

  readonly logout = rxMethod<void>(
    pipe(
      switchMap(() =>
        from(this.#authService.logout()).pipe(
          tapResponse(
            () => {
              this.#snackBar.open('Logout successful', '', { duration: 3000 });
              this.user.update(() => null);
              this.#router.navigate(['/login']);
            },
            (error: FirebaseError) => this.#snackBar.open(error.message, 'close')
          )
        )
      )
    )
  );

  readonly sendInvite = rxMethod<void>(
    pipe(
      switchMap(() =>
        this.#dialog
          .open(InviteUserDialogComponent, {
            width: '400px',
            disableClose: true
          })
          .afterClosed()
          .pipe(
            switchMap((email: string) =>
              email
                ? from(this.#authService.sendEmailVerification(email)).pipe(
                    tapResponse(
                      () =>
                        this.#snackBar.open(`invitation sent to ${email}`, '', { duration: 3000 }),
                      (error: FirebaseError) => {
                        this.#snackBar.open(error.message, 'close');
                      }
                    )
                  )
                : []
            )
          )
      )
    )
  );

  readonly verifyEmail = rxMethod<{ password?: string; signUpWithGoogle?: boolean }>(
    pipe(
      tap(() => patchState(this.#state, { signingUp: true })),
      withLatestFrom(this.#activatedRoute.queryParams),
      switchMap(([{ password, signUpWithGoogle }, queryParams]) => {
        const code = queryParams['code'];
        if (!code) {
          this.#router.navigate(['/login']);
          return [];
        }
        return from(
          this.#authService.verifyEmail(code, password ?? '', signUpWithGoogle ?? false)
        ).pipe(
          tapResponse(
            () => {
              this.#router.navigate(['/dashboard']);
              this.#snackBar.open('Email verified successfully', '', { duration: 3000 });
            },
            (error: FirebaseError) => {
              patchState(this.#state, { signingUp: false });
              this.#snackBar.open(error.message, 'close');
            },
            () => patchState(this.#state, { signingUp: false })
          )
        );
      })
    )
  );

  readonly fetchUsers = rxMethod<void>(
    pipe(
      tap(() => patchState(this.#state, { loading: true })),
      switchMap(() =>
        this.#authService.fetchUsers().pipe(
          tapResponse(
            ({ users }) => {
              patchState(this.#state, { users });
            },
            (error: HttpErrorResponse) => {
              this.#snackBar.open(error.error?.detail ?? error.message, 'close');
              patchState(this.#state, { loading: false });
            },
            () => patchState(this.#state, { loading: false })
          )
        )
      )
    )
  );

  readonly setCustomClaims = rxMethod<{
    uid: string;
    claims: Record<string, boolean | string | number>;
  }>(
    pipe(
      switchMap(({ uid, claims }) =>
        this.#authService.setCustomClaims(uid, claims).pipe(
          tapResponse(
            () => {
              this.#snackBar.open('Custom claims set successfully', '', { duration: 3000 });
              patchState(this.#state, (state) => ({
                ...state,
                users: state.users.map((user) => {
                  if (user.uid === uid) {
                    return { ...user, customClaims: claims };
                  }
                  return user;
                })
              }));
            },
            (error: HttpErrorResponse) => {
              this.#snackBar.open(error.error?.detail ?? error.message, 'close');
            }
          )
        )
      )
    )
  );

  readonly deleteUser = rxMethod<string>(
    pipe(
      switchMap((uid) =>
        this.#authService.deleteUser(uid).pipe(
          tapResponse(
            () => {
              this.#snackBar.open('User deleted successfully', '', { duration: 3000 });
              patchState(this.#state, (state) => ({
                ...state,
                users: state.users.filter((user) => user.uid !== uid)
              }));
            },
            (error: HttpErrorResponse) => {
              this.#snackBar.open(error.error?.detail ?? error.message, 'close');
            }
          )
        )
      )
    )
  );

  readonly toggleDisableUser = rxMethod<string>(
    pipe(
      switchMap((uid) =>
        this.#authService.toggleDisableUser(uid).pipe(
          tapResponse(
            () => {
              this.#snackBar.open('User disabled successfully', '', { duration: 3000 });
              patchState(this.#state, (state) => ({
                ...state,
                users: state.users.map((user) => {
                  if (user.uid === uid) {
                    return { ...user, disabled: !user.disabled };
                  }
                  return user;
                })
              }));
            },
            (error: HttpErrorResponse) => {
              this.#snackBar.open(error.error?.detail ?? error.message, 'close');
            }
          )
        )
      )
    )
  );

  readonly isSignInWithEmailLink = rxMethod<void>(
    pipe(
      switchMap(() =>
        from(this.#authService.isEmailLinkValid()).pipe(
          tapResponse(
            (isValid) => {
              if (!isValid) {
                this.#snackBar.open('Email link is not valid', 'close');
                this.#router.navigate(['/login']);
              }
            },
            (error: FirebaseError) => {
              this.#snackBar.open(error.message, 'close');
              this.#router.navigate(['/login']);
            }
          )
        )
      )
    )
  );

  constructor() {
    this.user$
      .pipe(
        filter((user) => !!user),
        switchMap((user) =>
          from(getIdTokenResult(user)).pipe(
            map(
              (tokenResult) =>
                ({ ...user, customClaims: tokenResult.claims }) as UserWithCustomClaims
            )
          )
        )
      )
      .subscribe((user) => {
        this.user.update(() => user);
      });
  }
}
